import React, { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { UTILITY_USER_ROLE_ADMIN } from 'common/utility/userRoles';
import { SR_API_ACTION_UTILITY_PROVIDER_GET_LIST } from 'api/utilityActionTypes';

import branch from 'client/core/branch';
import withScreenId from 'client/helpers/withScreenId';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import withScrollToTop from 'client/helpers/withScrollToTop';
import { SCREEN_ID_UTILITY_PROFIT_ADMIN_PROVIDERS } from 'client/constants/screenIds';

import UtilityProviderActions from 'client/actions/UtilityProvider';
import UtilityActivationActions from 'client/actions/UtilityActivation';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';
import UPProvidersTable from 'client/components/UPProvidersTable';

import { addNewButtonStyle } from './styles';

const PAGE_SIZE = 10;

@withTransitionHook
@withScreenId(SCREEN_ID_UTILITY_PROFIT_ADMIN_PROVIDERS)
@withScrollToTop
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN],
})
@branch({
  utilityUser: ['utilityUser'],
  utilityTypes: ['utilityActivations', 'types'],
  totalCount: ['utilityProviders', 'totalCount'],
  dispatch: 'dispatch',
})
class UtilityProfitAdminProvidersPage extends Component {
  static propTypes = {
    utilityTypes: PropTypes.array,
    totalCount: PropTypes.number,
    dispatch: PropTypes.func.isRequired,
  };

  static getPageMetadata = () => ({
    pageTitle: 'Providers Management | Utility Profit',
  });

  static fetchData(match, inject) {
    return Promise.all([
      UtilityProviderActions.getList(inject, {
        refresh: true,
      }),
      UtilityActivationActions.getTypesList(inject),
    ]);
  }

  constructor(props) {
    super(props);
    this.state = {
      showAddModal: false,
      showEditModal: false,
      currentProvider: null,
      isLoading: false,
      currentPage: 1,
      itemsPerPage: PAGE_SIZE,
      searchInput: '',
      selectedUtilityTypeId: '',
      // Cache providers by page number
      providersCache: {},
    };

    // Create a directly callable function for pagination/filter changes
    this._loadProviders = this._handleProvidersLoad;

    // Create a separate debounced function specifically for search
    this._debouncedSearch = debounce((searchInput) => {
      this._handleProvidersLoad({
        currentPage: 1,
        itemsPerPage: this.state.itemsPerPage,
        searchInput,
        selectedUtilityTypeId: this.state.selectedUtilityTypeId,
      });
    }, 600);
  }

  componentDidMount() {
    // Always load fresh data when the component mounts
    this._loadInitialData();
  }

  componentWillUnmount() {
    // Cancel the debounced function to prevent memory leaks
    if (this._debouncedSearch && this._debouncedSearch.cancel) {
      this._debouncedSearch.cancel();
    }
  }

  _loadInitialData = () => {
    const { currentPage, itemsPerPage, searchInput, selectedUtilityTypeId } = this.state;
    // Use the non-debounced version for initial load
    this._loadProviders({
      currentPage,
      itemsPerPage,
      searchInput,
      selectedUtilityTypeId,
    });
  };

  _toggleAddModal = () => {
    this.setState((prevState) => ({ showAddModal: !prevState.showAddModal }));
  };

  _toggleEditModal = (provider = null) => {
    this.setState((prevState) => ({
      showEditModal: !prevState.showEditModal,
      currentProvider: provider,
    }));
  };

  _handleProviderUpsert = (providerData) => {
    const payload = {
      title: providerData.name,
      url: providerData.url,
      phone: providerData.phone,
      utilityTypeId: providerData.utilityTypeId,
      energyInformationAdministrationId: providerData.energyInformationAdministrationId,
    };

    if (providerData.id) {
      payload.id = providerData.id;
    }

    const isEdit = !!providerData.id;

    this.props.dispatch(requestUtilityApi, {
      action: 'SR_API_ACTION_UTILITY_PROVIDER_UPSERT',
      payload,
      onSuccess: () => {
        global.alert(`Provider ${isEdit ? 'updated' : 'added'} successfully!`);
        this._loadInitialData();
      },
      onError: (error) => {
        global.alert(`Error ${isEdit ? 'updating' : 'adding'} provider: ${error.message}`);
      },
    });
  };

  // This is the router function that decides whether to use the debounced version or not
  _debouncedLoadProviders = (params) => {
    // If this is a search input change, use the debounced function
    if (params.searchInput !== undefined && params.searchInput !== this.state.searchInput) {
      // Pass only the search term to the debounced function
      this._debouncedSearch(params.searchInput);

      // Update local state to show the input value changing
      this.setState({
        searchInput: params.searchInput,
      });

      return;
    }

    // For non-search changes (pagination, items per page, utility type filter)
    // call the handler directly
    this._loadProviders(params);
  };

  _handleProvidersLoad = ({
    currentPage,
    itemsPerPage = PAGE_SIZE,
    searchInput = '',
    selectedUtilityTypeId = '',
  }) => {
    const skip = (currentPage - 1) * itemsPerPage;
    const cacheKey = `${skip}_${itemsPerPage}_${searchInput || ''}_${selectedUtilityTypeId || ''}`;

    // First set the UI state regardless of cache hit or miss
    this.setState({
      currentPage,
      itemsPerPage,
      searchInput,
      selectedUtilityTypeId,
      // Only show loading indicator if we don't have cached data
      isLoading: !this.state.providersCache[cacheKey],
    });

    // If we have cached data, just use it without an API call
    if (this.state.providersCache[cacheKey]) {
      return Promise.resolve();
    }

    // No cached data, make the API call
    return this.props.dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PROVIDER_GET_LIST,
      payload: {
        skip,
        take: itemsPerPage,
        title: searchInput || undefined,
        utilityTypeId: selectedUtilityTypeId || undefined,
      },
      onSuccess: (response) => {
        // Update the cache with the new data for this page
        this.setState((prevState) => ({
          isLoading: false,
          providersCache: {
            ...prevState.providersCache,
            [cacheKey]: response.list,
          },
        }));
      },
      onError: () => {
        this.setState({ isLoading: false });
      },
    });
  };

  // Get current providers from cache
  _getCurrentProviders = () => {
    const {
      currentPage,
      itemsPerPage,
      searchInput,
      selectedUtilityTypeId,
      providersCache,
    } = this.state;
    const skip = (currentPage - 1) * itemsPerPage;
    const cacheKey = `${skip}_${itemsPerPage}_${searchInput || ''}_${selectedUtilityTypeId || ''}`;

    return providersCache[cacheKey] || [];
  };

  render() {
    const { utilityTypes, totalCount } = this.props;
    const {
      showAddModal,
      showEditModal,
      currentProvider,
      isLoading,
      currentPage,
      itemsPerPage,
      searchInput,
      selectedUtilityTypeId,
    } = this.state;

    // Get providers from cache for current page/filters
    const currentProviders = this._getCurrentProviders();

    return (
      <SrPartnerPageV2
        navbarTitle="Utility Profit Providers"
        navbarImage="/images/utility-profit-logo.svg"
        backUrl="/utility-profit/admin"
        redirectToLastLocation={false}
        maxWidth="1200px"
      >
        <SrBox mt="20px">
          <SrFlex justify="between" align="center" columnGap="20px" mb="20px">
            <SrText font="greycliff" size="28" lineHeight="34px" weight="700">
              Utility Providers
            </SrText>

            <div onClick={this._toggleAddModal} style={addNewButtonStyle}>
              Add New Provider
            </div>
          </SrFlex>

          <UPProvidersTable
            providers={currentProviders}
            utilityTypes={utilityTypes || []}
            onEdit={this._toggleEditModal}
            totalCount={totalCount || 0}
            isLoading={isLoading}
            onLoadData={this._debouncedLoadProviders}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            searchInput={searchInput}
            selectedUtilityTypeId={selectedUtilityTypeId}
          />
        </SrBox>

        {showAddModal && (
          <UPProviderUpsertForm
            onCancel={this._toggleAddModal}
            utilityTypes={utilityTypes || []}
            onSubmit={this._handleProviderUpsert}
            isAdmin
            initialValues={{}}
            isNew
          />
        )}

        {showEditModal && (
          <UPProviderUpsertForm
            onCancel={this._toggleEditModal}
            utilityTypes={utilityTypes || []}
            onSubmit={this._handleProviderUpsert}
            isAdmin
            initialValues={currentProvider || {}}
            isNew={false}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

export default UtilityProfitAdminProvidersPage;
